/**
 * 绝对路径: D:/XXY/MeiShan_Project/frontend/shared/api/constants.ts
 * 全局常量/字典统一适配与缓存
 * - 后端接口：/api/v1/constants/*（服务类型/餐段/平台能力/菜系 等）
 * - 缓存：内存 + localStorage，依据 updated_at 或版本戳清理
 */

import { httpClient } from './http'

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function toRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {}
}

export interface CatalogItem {
  id: number
  code: string
  label: string
  is_active?: boolean
  sort_order?: number
  updated_at?: string
  status?: string
  parent?: number | null
  children?: CatalogItem[]
  // 可选扩展：菜品分类需要 group（combo|single|materials）
  group?: string
  // 可选扩展：餐段需要服务时间（MealSegmentCatalog 专属）
  service_start_time?: string
  service_end_time?: string
}

type CatalogKey =
  | 'service-types'
  | 'meal-segments'
  | 'capabilities'
  | 'cuisines'
  | 'spiciness-levels'
  | 'dish-categories'
  | 'merchant-brands'
  | 'enterprise-types'
  | 'enterprise-industries'
  | 'admin-divisions'
  | 'employee-role-tags'

const LOOKUP_KEY: Record<string, string> = {
  'service-types': 'service-types',
  'meal-segments': 'meal-segments',
  'capabilities': 'capabilities',
  'cuisines': 'cuisines',
  'dish-categories': 'dish-categories',
  'merchant-brands': 'merchant-brands',
  'enterprise-types': 'enterprise-types',
  'enterprise-industries': 'enterprise-industries',
  'admin-divisions': 'admin-divisions',
  'employee-role-tags': 'employee-role-tags',
}

function resolveKey(key: CatalogKey): string {
  return LOOKUP_KEY[key] || key
}

const MEMORY_CACHE: Record<string, { ts: number; items: CatalogItem[] }> = {}
const LS_KEY_PREFIX = 'constants:'

function getActiveOperatorKey(): string {
  try {
    const opId = localStorage.getItem('activeOperatorId')
    return opId && String(opId).trim() ? `op:${String(opId).trim()}:` : 'op:global:'
  } catch {
    return 'op:global:'
  }
}
const STALE_MS = 1000 * 60 * 60 // 1h

function lsKey(key: string) { return `${LS_KEY_PREFIX}${getActiveOperatorKey()}${key}` }

function readLocal(key: string): CatalogItem[] | null {
  try {
    const raw = localStorage.getItem(lsKey(key))
    if (!raw) return null
    const data = JSON.parse(raw)
    if (!Array.isArray(data?.items)) return null
    // 过期判断
    if (typeof data.ts === 'number' && Date.now() - data.ts < STALE_MS) {
      return data.items
    }
    return null
  } catch { return null }
}

function writeLocal(key: string, items: CatalogItem[]): void {
  try { localStorage.setItem(lsKey(key), JSON.stringify({ ts: Date.now(), items })) } catch {}
}

// 行政区划容错：将对象/映射/包裹层转为数组
// NOTE: 当前未被使用，保留逻辑请在实际调用点引入；避免 lint “unused” 噪音。

function normalize(items: unknown[]): CatalogItem[] {
  const asObj = (v: unknown): Record<string, unknown> =>
    v && typeof v === 'object' ? (v as Record<string, unknown>) : {}

  return (items || []).map((raw) => {
    const it = asObj(raw)
    const childrenRaw = it.children
    const parentRaw = it.parent
    const parent =
      parentRaw === undefined || parentRaw === null
        ? null
        : typeof parentRaw === 'number'
          ? parentRaw
          : typeof parentRaw === 'string'
            ? (Number.isFinite(Number(parentRaw)) ? Number(parentRaw) : null)
            : null
    return {
    id: Number(it.id ?? 0),
    code: String(it.code ?? ''),
    label: String(it.label ?? ''),
    is_active: Boolean(it.is_active ?? true),
    sort_order: typeof it.sort_order === 'number' ? it.sort_order : undefined,
    updated_at: typeof it.updated_at === 'string' ? it.updated_at : undefined,
    status: typeof it.status === 'string' ? it.status : undefined,
      parent,
      children: Array.isArray(childrenRaw) ? normalize(childrenRaw) : undefined,
    group: typeof it.group === 'string' ? it.group : undefined,
    service_start_time: typeof it.service_start_time === 'string' ? it.service_start_time : undefined,
    service_end_time: typeof it.service_end_time === 'string' ? it.service_end_time : undefined,
    }
  })
}

async function fetchList(key: CatalogKey, tree = false, activeOnly = true): Promise<CatalogItem[]> {
  const actualKey = resolveKey(key)
  const opPrefix = getActiveOperatorKey()
  const cacheKey = `${opPrefix}${actualKey}${tree ? ':tree' : ''}${activeOnly ? ':active' : ''}`

  if (MEMORY_CACHE[cacheKey] && Date.now() - MEMORY_CACHE[cacheKey].ts < STALE_MS) {
    return MEMORY_CACHE[cacheKey].items
  }
  const fromLs = readLocal(cacheKey)
  if (fromLs) {
    MEMORY_CACHE[cacheKey] = { ts: Date.now(), items: fromLs }
    // 后台刷新
    refresh(cacheKey, actualKey, tree, activeOnly).catch(() => {})
    return fromLs
  }
  // 直接拉取
  const items = await requestRemote(actualKey, tree, activeOnly)
  MEMORY_CACHE[cacheKey] = { ts: Date.now(), items }
  writeLocal(cacheKey, items)
  return items
}

async function refresh(cacheKey: string, key: string, tree: boolean, activeOnly: boolean) {
  try {
    const items = await requestRemote(key, tree, activeOnly)
    MEMORY_CACHE[cacheKey] = { ts: Date.now(), items }
    writeLocal(cacheKey, items)
  } catch {}
}

async function requestRemote(key: string, tree: boolean, activeOnly: boolean): Promise<CatalogItem[]> {
  // 注意：不要以斜杠开头，保持与 axios baseURL 拼接（/api/v1）
  const base = `constants/${key}/`
  let url = base
  const params: Record<string, unknown> = {}
  if (activeOnly) { params.is_active = true; params.active_only = true }
  // 常量列表通常数据量不大，使用较大的 page_size 避免分页问题
  params.page_size = 1000
  // 行政区划后端不支持 tree/ 子路由，采用 tree=true 查询参数
  if (tree) {
    if (key !== 'admin-divisions') {
      url = `${base}tree/`
    }
  }
  // 使用原始 axios 实例，兼容 {data: ...} 与原生 DRF 响应
  const resp = await httpClient.getAxiosInstance().get(url, { params })
  const raw: unknown = resp.data
  let items: unknown[] = []
  if (key === 'admin-divisions') {
    // 优先使用后端提供的按层级查询（level/parent）拼装树，避免对 tree/ 或 parent= 空的依赖
    if (tree) {
      try {
        const byLevel = await buildAdminDivisionTreeViaLevel(activeOnly)
        if (Array.isArray(byLevel) && byLevel.length) {
          return normalize(byLevel)
        }
      } catch {}
    }
    // 拉取尽可能多的数据，避免分页丢失子级
    if (tree) { params.page_size = 10000 }
    // 行政区划后端可能返回：
    // 1) 对象映射 { '44': '广东省', ... } 或 { '44': { label/name, children: {...} }, ... }
    // 2) {data|results|items} 包裹
    const rawRec = toRecord(raw)
    const loose = (rawRec.data || rawRec.results || rawRec.items) ?? raw
    if (Array.isArray(loose)) {
      items = loose
    } else if (isRecord(loose) && Array.isArray(loose.items)) {
      const arr: unknown[] = loose.items as unknown[]
      // 若已经是树（首元素含 children），直接返回
      if (arr.length > 0 && isRecord(arr[0]) && Array.isArray(toRecord(arr[0]).children)) {
        items = arr
      } else {
        // 平铺结构 → 构建树
        const flat: Array<{ code: string; label?: string; name?: string; parent?: string | null | number }>
          = arr as unknown as Array<{ code: string; label?: string; name?: string; parent?: string | null | number }>
        const nodeMap = new Map<string, { code: string; label: string; children: unknown[] }>()
        for (const it of flat) {
          const code = String(it.code)
          nodeMap.set(code, { code, label: String(it.label ?? it.name ?? code), children: [] })
        }
        const roots: unknown[] = []
        for (const it of flat) {
          const code = String(it.code)
          const parent = it.parent
          const p = parent == null || parent === '' || parent === 0 ? null : String(parent)
          if (p && nodeMap.has(p)) {
            nodeMap.get(p)!.children.push(nodeMap.get(code)!)
          } else {
            roots.push(nodeMap.get(code)!)
          }
        }
        items = roots
      }
    } else if (loose && typeof loose === 'object') {
      const convert = (obj: unknown): unknown[] => {
        const rec = toRecord(obj)
        return Object.entries(rec).map(([code, val]) => {
          const value = val
          const label = (typeof value === 'string')
            ? String(value)
            : (String(toRecord(value).label ?? toRecord(value).name ?? code))
          const vr = toRecord(value)
          const childrenSrc = isRecord(value) ? (vr.children || vr.child || vr.items) : undefined
          const children = childrenSrc
            ? (Array.isArray(childrenSrc) ? childrenSrc : convert(childrenSrc))
            : undefined
          return { code, label, children }
        })
      }
      items = convert(loose)
    } else {
      items = []
    }
  } else {
    const r = toRecord(raw)
    items = Array.isArray(raw)
      ? raw
      : (Array.isArray(r.results) ? r.results : (Array.isArray(r.data) ? r.data : []))
  }
  return normalize(items)
}

/**
 * 通过 level/parent 三段查询拼装省市区树（后端 swagger 至少提供此模式）。
 */
async function buildAdminDivisionTreeViaLevel(activeOnly: boolean): Promise<Array<{ code: string; label: string; children?: unknown[] }>> {
  const axios = httpClient.getAxiosInstance()
  const base = `constants/admin-divisions/`
  const common = activeOnly ? { is_active: true, active_only: true } : {}

  const get = async (params: Record<string, unknown>): Promise<unknown[]> => {
    const r = await axios.get(base, { params })
    const data: unknown = r.data
    if (Array.isArray(data)) return data
    const d = toRecord(data)
    const nested = toRecord(d.data)
    if (Array.isArray(nested.items)) return nested.items as unknown[]
    if (Array.isArray(d.results)) return d.results as unknown[]
    if (Array.isArray(d.items)) return d.items as unknown[]
    if (Array.isArray(d.data)) return d.data as unknown[]
    return []
  }

  // 1) 省
  const provinces = await get({ ...common, level: 'province' })
  if (!Array.isArray(provinces) || provinces.length === 0) return []

  // 2) 市（分省并行）
  const provinceNodes: Array<{ code: string; label: string; children: unknown[] }> = provinces.map((p) => {
    const pr = toRecord(p)
    return { code: String(pr.code ?? ''), label: String(pr.label ?? pr.name ?? pr.code ?? ''), children: [] }
  })
  await Promise.all(provinceNodes.map(async (p) => {
    try {
      const cities = await get({ ...common, level: 'city', parent: p.code })
      const cityNodes: Array<{ code: string; label: string; children: unknown[] }> = cities.map((c) => {
        const cr = toRecord(c)
        return { code: String(cr.code ?? ''), label: String(cr.label ?? cr.name ?? cr.code ?? ''), children: [] }
      })
      p.children = cityNodes
      // 3) 区（分市并行）
      await Promise.all(cityNodes.map(async (c) => {
            try {
              // 暂时跳过逐 city 拉取，避免请求风暴；后续可按需懒加载
              c.children = c.children || []
            } catch { c.children = c.children || [] }
      }))
    } catch { p.children = p.children || [] }
  }))

  return provinceNodes
}

// 运行期探测：常量口径是否可用（避免持续 404 噪音）
const _g = globalThis as unknown as Record<string, unknown>
let ROLE_TAGS_CONSTANTS_AVAILABLE: boolean | null = (typeof _g.__ROLE_TAGS_CONST_AVAIL__ === 'boolean'
  ? (_g.__ROLE_TAGS_CONST_AVAIL__ as boolean)
  : null)
// 环境开关：禁用 constants 口径（始终走回退）
const DISABLE_ROLE_TAGS_CONSTANTS = String((import.meta.env as unknown as Record<string, unknown>).VITE_DISABLE_ROLE_TAGS_CONSTANTS ?? 'false') === 'true'

export const constantsApi = {
  /**
   * 获取服务类型列表（业务常量，仍然有效）
   * 
   * ⚠️ 重要纠正：此接口并未废弃！
   * 
   * service_type (服务类型) 是业务常量，如：堂食、外卖、团餐等
   * consumption_type (消费类型) 是固定枚举，如：线上订餐、到店消费等
   * 两者通过 getMappings() 的映射表关联
   */
  listServiceTypes: (activeOnly = true) => fetchList('service-types', false, activeOnly),
  
  /**
   * 获取常量映射接口
   * 
   * @returns {Promise<{ service_type_to_consumption_type: Array, consumption_types: Array }>}
   * 
   * 接口：GET /api/v1/constants/mappings/
   * 
   * 说明：
   * - service_type_to_consumption_type：服务类型 → 消费类型的映射关系（当前为空，后续补充）
   * - consumption_types：消费类型列表（从数据库 ServiceTypeCatalog 表读取）
   * 
   * ✅ 2025-11-19 修复：consumption_types 现在返回数据库真实数据
   * - code 格式：拼音 code（如 ALWS01、BLWM01、CLGR01 等）
   * - 数据来源：ServiceTypeCatalog 表
   * - 示例：[{code: "ALWS01", label: "线上订餐"}, {code: "BLWM01", label: "到店消费"}, ...]
   * 
   * ⚠️ 注意：企业的 service_types 字段返回的就是这些拼音 code
   */
  async getMappings(): Promise<{
    service_type_to_consumption_type: Array<{ service_type: string; consumption_type: string }>
    consumption_types: Array<{ code: string; label: string }>
  }> {
    // ✅ 使用 httpClient.get() 而不是 getAxiosInstance().get()
    // httpClient.get() 会自动提取响应中的 data 字段（通过 extractPayload 方法）
    const resp = await httpClient.get<{
      service_type_to_consumption_type: Array<{ service_type: string; consumption_type: string }>
      consumption_types: Array<{ code: string; label: string }>
    }>('constants/mappings/')
    return resp
  },
  
  listMealSegments: (activeOnly = true) => fetchList('meal-segments', false, activeOnly),
  treeMealSegments: (activeOnly = true) => fetchList('meal-segments', true, activeOnly),
  listCapabilities: (activeOnly = true) => fetchList('capabilities', false, activeOnly),
  listCuisines: (activeOnly = true) => fetchList('cuisines', false, activeOnly),
  listSpicinessLevels: async () => {
    const axios = httpClient.getAxiosInstance()
    const resp = await axios.get('constants/spiciness-levels/')
    const raw: unknown = resp.data
    const nested = isRecord(raw) ? (toRecord(raw).data ?? raw) : raw
    const n = toRecord(nested)
    const items = Array.isArray(n.items) ? (n.items as unknown[]) : []
    return items.map((it) => {
      const r = toRecord(it)
      return { code: String(r.code ?? ''), label: String(r.label ?? ''), value: r.value, order: r.order }
    })
  },
  // 菜品分类=菜品类型：后端暂未返回 group。前端从树的根节点 label 推断 group，并向下传播。
  async listDishCategories(activeOnly = true): Promise<CatalogItem[]> {
    // 优先尝试树接口；若后端未提供 tree/，则回退为平铺列表
    let tree: CatalogItem[] = []
    try {
      tree = await fetchList('dish-categories', true, activeOnly)
    } catch {
      tree = await fetchList('dish-categories', false, activeOnly)
    }
    const flat: CatalogItem[] = []
    const detectGroup = (label: string, code?: string): 'combo' | 'single' | 'materials' => {
      const sCn = String(label || '')
      const s = sCn.toLowerCase()
      // code 映射（如后端 code 与历史一致）
      const CODE_TO_GROUP: Record<string, 'combo'|'single'|'materials'> = {
        chinese_stir_fry: 'combo', lu_wei_roast: 'combo', jp_western: 'combo', others: 'combo',
        burger_fried_chicken: 'single', pasta_pizza: 'single', snacks_skewer: 'single', wrap_bun_pancake: 'single',
        porridge_noodle: 'single', dumpling_wonton: 'single', cold_dish: 'single', dessert_drink: 'single',
        soup_geng: 'single', cn_breakfast: 'single', buffet: 'single', bakery_west_breakfast: 'single', fruit: 'single',
        hotpot_malatang: 'materials', light_salad: 'materials'
      }
      if (code && CODE_TO_GROUP[code]) return CODE_TO_GROUP[code]
      // 中文关键词兜底
      const hasAny = (arr: string[]) => arr.some(k => sCn.includes(k) || s.includes(k))
      if (hasAny(['单品'])) return 'single'
      if (hasAny(['汉堡','炸鸡','意面','披萨','小吃','串','夹','馍','饼','包','粥','粉','面','饺子','馄饨','凉菜','甜点','饮品','汤','羹','早餐','自助','烘培','面包','水果'])) return 'single'
      if (hasAny(['自选','材料','火锅','麻辣烫','冒菜','香锅','干锅','轻食','沙拉','健康'])) return 'materials'
      if (hasAny(['主菜'])) return 'combo'
      return 'combo'
    }
    const walk = (nodes: CatalogItem[], currentGroup?: 'combo'|'single'|'materials') => {
      for (const n of nodes || []) {
        const group = (n.group as 'combo'|'single'|'materials') || currentGroup || detectGroup(n.label, n.code)
        const node: CatalogItem = { ...n, group }
        flat.push(node)
        if (Array.isArray(n.children) && n.children.length) {
          walk(n.children, group)
        }
      }
    }
    walk(tree)
    return flat
  },
  listMerchantBrands: (activeOnly = true) => fetchList('merchant-brands', false, activeOnly),
  listEnterpriseTypes: (activeOnly = true) => fetchList('enterprise-types', false, activeOnly),
  listEnterpriseIndustries: (activeOnly = true) => fetchList('enterprise-industries', false, activeOnly),
  listAdminDivisions: (tree = true) => fetchList('admin-divisions', tree, true),
  async listAdminDivisionChildren(parentCode: string): Promise<CatalogItem[]> {
    const url = `constants/admin-divisions/`
    const params: Record<string, unknown> = { parent: parentCode, is_active: true, active_only: true }
    const resp = await httpClient.getAxiosInstance().get(url, { params })
    const raw: unknown = resp.data
    let items: unknown[] = []
    const nested = isRecord(raw) ? (toRecord(raw).data ?? raw) : raw
    const n = toRecord(nested)
    if (Array.isArray(nested)) {
      items = nested
    } else if (Array.isArray(n.items)) {
      items = n.items as unknown[]
    } else if (Array.isArray(n.results)) {
      items = n.results as unknown[]
    } else if (Array.isArray(n.data)) {
      items = n.data as unknown[]
    } else if (nested && typeof nested === 'object') {
      // 对象映射
      items = Object.entries(nested as Record<string, unknown>).map(([code, val]) => {
        const v = toRecord(val)
        return { code, label: typeof val === 'string' ? val : String(v.label ?? v.name ?? code) }
      })
    }
    return normalize(items)
  },
  async listEmployeeRoleTags(activeOnly = true): Promise<CatalogItem[]> {
    try {
      // 首选规范路径：/api/v1/constants/employee-role-tags/
      if (!DISABLE_ROLE_TAGS_CONSTANTS && ROLE_TAGS_CONSTANTS_AVAILABLE !== false) {
        const items = await fetchList('employee-role-tags', false, activeOnly)
        // 若可达，记忆可用
        ROLE_TAGS_CONSTANTS_AVAILABLE = true
        ;(globalThis as unknown as Record<string, unknown>).__ROLE_TAGS_CONST_AVAIL__ = true
        return items
      }
    } catch {
      // 记忆不可用，走回退
      ROLE_TAGS_CONSTANTS_AVAILABLE = false
      ;(globalThis as unknown as Record<string, unknown>).__ROLE_TAGS_CONST_AVAIL__ = false
    }
    // 回退兼容：/api/v1/employee-role-tags/
    try {
      const url = `employee-role-tags/`
      const params: Record<string, unknown> = { is_active: true, active_only: activeOnly }
      const resp = await httpClient.getAxiosInstance().get(url, { params })
      const raw: unknown = resp.data
      const r = toRecord(raw)
      const items: unknown[] = Array.isArray(raw)
        ? raw
        : (Array.isArray(r.results) ? (r.results as unknown[]) : (Array.isArray(r.data) ? (r.data as unknown[]) : []))
      return normalize(items)
    } catch {
      return []
    }
  },
  async listLegalHolidaysByYear(year: number): Promise<Array<{ date: string; label: string; is_working_day?: boolean; is_legal_holiday?: boolean }>> {
    // GET constants/holidays/{year}/  直接使用 axios 实例，保持 BASE 拼接
    const url = `constants/holidays/${String(year)}/`
    const resp = await httpClient.getAxiosInstance().get(url)
    const raw: unknown = resp.data
    
    // 后端返回格式：{ data: { holidays: [...], workdays: [...], version: "..." } }
    const dataWrapper = (isRecord(raw) ? (toRecord(raw).data ?? raw) : raw)
    const dw = toRecord(dataWrapper)
    const holidays = Array.isArray(dw.holidays) ? (dw.holidays as unknown[]) : []
    const workdays = Array.isArray(dw.workdays) ? (dw.workdays as unknown[]) : []
    
    // 合并节假日和工作日，标注类型
    const result: Array<{ date: string; label: string; is_working_day?: boolean; is_legal_holiday?: boolean }> = []
    
    // 添加节假日（休息日）
    holidays.forEach((it: unknown) => {
      // 后端 HolidaysResponse 的 holidays/workdays 为 string[]（YYYY-MM-DD）
      const dateStr =
        typeof it === 'string'
          ? it
          : (() => {
              const r = toRecord(it)
              return String(r.date || r.day || r.ds || '')
            })()
      const date = String(dateStr || '').trim()
      if (!date) return
      result.push({
        date,
        label: '休',
        is_working_day: false,
        is_legal_holiday: true
      })
    })
    
    // 添加工作日（调休上班）
    workdays.forEach((it: unknown) => {
      const dateStr =
        typeof it === 'string'
          ? it
          : (() => {
              const r = toRecord(it)
              return String(r.date || r.day || r.ds || '')
            })()
      const date = String(dateStr || '').trim()
      if (!date) return
      result.push({
        date,
        label: '班',
        is_working_day: true,
        is_legal_holiday: false
      })
    })
    
    return result
  },
}

export type { CatalogKey }


/**
 * 将 code 数组映射为标签数组
 * 输入：codes（服务类型 code 列表）、dict（从 constantsApi 获取的字典项）
 * 输出：对应的中文标签列表（找不到则回退为原 code）
 */
export function mapCodesToLabels(codes: string[], dict: CatalogItem[]): string[] {
  const map = new Map<string, string>()
  const walk = (items: CatalogItem[]) => {
    for (const it of items) {
      map.set(it.code, it.label)
      if (it.children?.length) walk(it.children)
    }
  }
  walk(dict)
  return (codes || []).map(c => map.get(String(c)) || String(c))
}
